"use client"

import { useState, useEffect } from "react"
import { useSession } from "next-auth/react"
import {
    Castle,
    Shield,
    Swords,
    Crown,
    Users,
    Clock,
    ChevronDown,
    ChevronUp,
    Landmark,
    ShieldAlert,
    ShieldCheck,
    Flame,
    Timer,
} from "lucide-react"

/* ── Types ── */
interface ClanInfo {
    clan_id: number
    clan_name: string
    clan_level: number
    leader_name: string
    members: number
    reputation?: number
}

interface SiegeClan {
    clan_id: number
    clan_name: string
    clan_level: number
    type?: number
}

interface CastleData {
    id: number
    name: string
    territory: string
    siegeDate: number
    treasury: number
    owner: ClanInfo | null
    attackers: SiegeClan[]
    defenders: SiegeClan[]
}

interface FortData {
    id: number
    name: string
    siegeDate: number
    owner: ClanInfo | null
    registrations: SiegeClan[]
    state: number
}

/* ── Castle imagery config ── */
const CASTLE_COLORS: Record<number, { gradient: string; accent: string; ring: string }> = {
    1: { gradient: "from-emerald-900/40 to-emerald-950/60", accent: "text-emerald-400", ring: "ring-emerald-500/30" },
    2: { gradient: "from-sky-900/40 to-sky-950/60", accent: "text-sky-400", ring: "ring-sky-500/30" },
    3: { gradient: "from-amber-900/40 to-amber-950/60", accent: "text-amber-400", ring: "ring-amber-500/30" },
    4: { gradient: "from-violet-900/40 to-violet-950/60", accent: "text-violet-400", ring: "ring-violet-500/30" },
    5: { gradient: "from-red-900/40 to-red-950/60", accent: "text-red-400", ring: "ring-red-500/30" },
    6: { gradient: "from-cyan-900/40 to-cyan-950/60", accent: "text-cyan-400", ring: "ring-cyan-500/30" },
    7: { gradient: "from-orange-900/40 to-orange-950/60", accent: "text-orange-400", ring: "ring-orange-500/30" },
    8: { gradient: "from-indigo-900/40 to-indigo-950/60", accent: "text-indigo-400", ring: "ring-indigo-500/30" },
    9: { gradient: "from-rose-900/40 to-rose-950/60", accent: "text-rose-400", ring: "ring-rose-500/30" },
}

const DEFAULT_COLORS = { gradient: "from-slate-900/40 to-slate-950/60", accent: "text-slate-400", ring: "ring-slate-500/30" }

/* ── Helpers ── */
function formatSiegeDate(timestamp: number): string {
    if (!timestamp || timestamp === 0) return "Not scheduled"
    const date = new Date(timestamp)
    return date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    })
}

function getTimeUntilSiege(timestamp: number): string {
    if (!timestamp || timestamp === 0) return ""
    const now = Date.now()
    const diff = timestamp - now
    if (diff <= 0) return "In progress or passed"
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    if (days > 0) return `${days}d ${hours}h`
    if (hours > 0) return `${hours}h ${mins}m`
    return `${mins}m`
}

/* ── Castle Card ── */
function CastleCard({
    castle,
    leaderClanId,
    isRegistered,
    onRegister,
    onUnregister,
    isAdmin,
    onSchedule,
}: {
    castle: CastleData
    leaderClanId: number | null
    isRegistered: boolean
    onRegister: (castleId: number) => void
    onUnregister: (castleId: number) => void
    isAdmin: boolean
    onSchedule: (castleId: number, ts: number) => void
}) {
    const [expanded, setExpanded] = useState(false)
    const colors = CASTLE_COLORS[castle.id] || DEFAULT_COLORS
    const timeLeft = getTimeUntilSiege(castle.siegeDate)
    const isScheduled = castle.siegeDate > 0

    return (
        <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${colors.gradient} border border-white/5 ring-1 ${colors.ring} transition-all duration-300 hover:border-white/10`}>
            {/* Header */}
            <div className="relative p-6">
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                        <div className={`flex h-14 w-14 items-center justify-center rounded-xl bg-black/30 ring-1 ${colors.ring}`}>
                            <Castle className={`h-7 w-7 ${colors.accent}`} />
                        </div>
                        <div>
                            <h3 className="text-xl font-black text-white tracking-tight">{castle.name} Castle</h3>
                            <p className="text-xs font-medium text-slate-500 uppercase tracking-widest mt-0.5">{castle.territory}</p>
                        </div>
                    </div>
                    {isScheduled && (
                        <div className="flex items-center gap-2 rounded-full bg-black/30 px-3 py-1.5 ring-1 ring-white/10">
                            <Timer className="h-3.5 w-3.5 text-amber-400" />
                            <span className="text-xs font-bold text-amber-400 tabular-nums">{timeLeft}</span>
                        </div>
                    )}
                </div>

                {/* Owner Section */}
                <div className="mt-5 rounded-xl bg-black/20 p-4 ring-1 ring-white/5">
                    {castle.owner ? (
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-500/10 ring-1 ring-amber-500/20">
                                    <Crown className="h-5 w-5 text-amber-400" />
                                </div>
                                <div>
                                    <p className="text-sm font-black text-white">{castle.owner.clan_name}</p>
                                    <p className="text-[11px] text-slate-500">
                                        Led by <span className="text-slate-300 font-semibold">{castle.owner.leader_name}</span>
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-slate-400">
                                <div className="flex items-center gap-1.5">
                                    <Shield className="h-3.5 w-3.5" />
                                    <span className="font-bold">Lv.{castle.owner.clan_level}</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <Users className="h-3.5 w-3.5" />
                                    <span className="font-bold">{castle.owner.members}</span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-800 ring-1 ring-slate-700">
                                <ShieldAlert className="h-5 w-5 text-slate-600" />
                            </div>
                            <div>
                                <p className="text-sm font-bold text-slate-500">Unclaimed</p>
                                <p className="text-[11px] text-slate-600">No clan owns this castle</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Siege Date */}
                <div className="mt-3 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Clock className="h-3.5 w-3.5" />
                        <span className="font-medium">{isScheduled ? formatSiegeDate(castle.siegeDate) : "No siege scheduled"}</span>
                    </div>
                    {castle.treasury > 0 && (
                        <span className="text-[11px] font-bold text-amber-500/60 tabular-nums">
                            Treasury: {castle.treasury.toLocaleString()} Adena
                        </span>
                    )}
                </div>

                {/* Expand for registrations */}
                {(castle.attackers.length > 0 || castle.defenders.length > 0) && (
                    <button
                        onClick={() => setExpanded(!expanded)}
                        className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg bg-white/[0.03] py-2 text-xs font-bold text-slate-400 hover:bg-white/[0.06] hover:text-white transition-colors"
                    >
                        {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                        Siege Registrations ({castle.attackers.length + castle.defenders.length})
                    </button>
                )}

                {/* Action buttons */}
                <div className="px-6 pb-6">
                    <div className="flex items-center gap-2">
                        {leaderClanId ? (
                            isRegistered ? (
                                <button onClick={() => onUnregister(castle.id)} className="rounded-full bg-red-600 px-4 py-2 text-xs font-bold text-white">Unregister</button>
                            ) : (
                                <button onClick={() => onRegister(castle.id)} className="rounded-full bg-emerald-600 px-4 py-2 text-xs font-bold text-white">Register Clan</button>
                            )
                        ) : (
                            <div className="text-xs text-slate-400">Only clan leaders can register</div>
                        )}

                        {isAdmin && (
                            <ScheduleControl castle={castle} onSchedule={onSchedule} />
                        )}
                    </div>
                </div>
            </div>

            {/* Expanded registrations */}
            {expanded && (
                <div className="border-t border-white/5 px-6 pb-5 pt-4 space-y-4">
                    {castle.attackers.length > 0 && (
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <Swords className="h-3.5 w-3.5 text-red-400" />
                                <span className="text-[11px] font-black text-red-400 uppercase tracking-widest">Attackers</span>
                            </div>
                            <div className="space-y-1">
                                {castle.attackers.map((clan) => (
                                    <div key={clan.clan_id} className="flex items-center justify-between rounded-lg bg-red-500/5 px-3 py-2 ring-1 ring-red-500/10">
                                        <span className="text-sm font-bold text-white">{clan.clan_name}</span>
                                        <span className="text-xs text-slate-500">Lv.{clan.clan_level}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {castle.defenders.length > 0 && (
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <ShieldCheck className="h-3.5 w-3.5 text-blue-400" />
                                <span className="text-[11px] font-black text-blue-400 uppercase tracking-widest">Defenders</span>
                            </div>
                            <div className="space-y-1">
                                {castle.defenders.map((clan) => (
                                    <div key={clan.clan_id} className="flex items-center justify-between rounded-lg bg-blue-500/5 px-3 py-2 ring-1 ring-blue-500/10">
                                        <span className="text-sm font-bold text-white">{clan.clan_name}</span>
                                        <span className="text-xs text-slate-500">Lv.{clan.clan_level}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

/* ── Fort Card ── */
function FortCard({ fort }: { fort: FortData }) {
    const isOwned = !!fort.owner
    const timeLeft = getTimeUntilSiege(fort.siegeDate)
    const isScheduled = fort.siegeDate > 0

    return (
        <div className="rounded-xl bg-slate-900/40 border border-white/5 p-4 hover:border-white/10 transition-all">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-black/30 ring-1 ring-white/10">
                        <Landmark className="h-5 w-5 text-slate-400" />
                    </div>
                    <div>
                        <h4 className="text-sm font-bold text-white">{fort.name}</h4>
                        {isOwned ? (
                            <p className="text-[11px] text-slate-500">
                                Owned by <span className="text-amber-400 font-semibold">{fort.owner!.clan_name}</span>
                            </p>
                        ) : (
                            <p className="text-[11px] text-slate-600">Unclaimed</p>
                        )}
                    </div>
                </div>
                <div className="text-right">
                    {isScheduled && timeLeft && (
                        <div className="flex items-center gap-1.5 text-xs text-amber-400">
                            <Timer className="h-3 w-3" />
                            <span className="font-bold tabular-nums">{timeLeft}</span>
                        </div>
                    )}
                    {fort.registrations.length > 0 && (
                        <p className="text-[10px] text-slate-600 mt-0.5">
                            {fort.registrations.length} registered
                        </p>
                    )}
                </div>
            </div>
        </div>
    )
}

function ScheduleControl({ castle, onSchedule }: { castle: CastleData; onSchedule: (id: number, ts: number) => void }) {
    const [dt, setDt] = useState("")
    return (
        <div className="ml-auto flex items-center gap-2">
            <input type="datetime-local" value={dt} onChange={(e) => setDt(e.target.value)} className="rounded-md bg-black/20 px-2 py-1 text-xs text-slate-200" />
            <button
                onClick={() => {
                    if (!dt) return alert("Select date/time")
                    const ts = new Date(dt).getTime()
                    onSchedule(castle.id, ts)
                }}
                className="rounded-md bg-yellow-600 px-3 py-1 text-xs font-bold text-black"
            >
                Schedule
            </button>
        </div>
    )
}

/* ── Main Page ── */
export default function SiegePage() {
    const [castles, setCastles] = useState<CastleData[]>([])
    const [forts, setForts] = useState<FortData[]>([])
    const [loading, setLoading] = useState(true)
    const [tab, setTab] = useState<"castles" | "forts">("castles")
    const [now, setNow] = useState(Date.now())
    const { data: session } = useSession()
    const [leaderInfo, setLeaderInfo] = useState<{ clan_id: number; clan_name: string } | null>(null)
    const [myRegs, setMyRegs] = useState<any[]>([])

    // Live timer tick
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000)
        return () => clearInterval(id)
    }, [])

    const fetchAll = () => {
        setLoading(true)
        Promise.all([fetch("/api/siege").then((r) => r.json()), fetch("/api/siege/me").then((r) => r.json()).catch(() => null)])
            .then(([siegedata, me]) => {
                setCastles(siegedata.castles || [])
                setForts(siegedata.forts || [])
                if (me && me.leader) setLeaderInfo(me.leader)
                if (me && me.registrations) setMyRegs(me.registrations)
            })
            .catch(console.error)
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        fetchAll()
    }, [session])

    const refresh = () => fetchAll()

    const onRegister = async (castleId: number) => {
        const res = await fetch("/api/siege/register", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ castleId, type: 1 }) })
        const data = await res.json()
        if (res.ok) alert(data.message || "Registered")
        else alert(data.error || "Failed")
        refresh()
    }

    const onUnregister = async (castleId: number) => {
        const res = await fetch(`/api/siege/register?castleId=${castleId}`, { method: "DELETE" })
        const data = await res.json()
        if (res.ok) alert(data.message || "Unregistered")
        else alert(data.error || "Failed")
        refresh()
    }

    const onSchedule = async (castleId: number, ts: number) => {
        const res = await fetch("/api/siege/admin", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "schedule", castleId, siegeDate: ts }) })
        const data = await res.json()
        if (res.ok) alert(data.message || "Scheduled")
        else alert(data.error || "Failed")
        refresh()
    }

    const ownedCastles = castles.filter((c) => c.owner)
    const upcomingSieges = castles.filter((c) => c.siegeDate > now)

    return (
        <div className="min-h-screen bg-slate-950">
            {/* Hero */}
            <div className="relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-red-950/20 via-slate-950 to-slate-950" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(239,68,68,0.08),transparent_60%)]" />
                <div className="relative container mx-auto px-4 pt-32 pb-12">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-red-500/10 ring-1 ring-red-500/20">
                            <Flame className="h-6 w-6 text-red-500" />
                        </div>
                        <div>
                            <h1 className="text-4xl font-black text-white tracking-tight uppercase">
                                Siege <span className="text-red-500">War</span>
                            </h1>
                            <p className="text-sm text-slate-500 font-medium">Castle & Fortress siege information</p>
                        </div>
                    </div>

                    {/* Stats Row */}
                    <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="rounded-xl bg-slate-900/50 border border-white/5 p-4">
                            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Castles</p>
                            <p className="text-2xl font-black text-white tabular-nums">{castles.length}</p>
                        </div>
                        <div className="rounded-xl bg-slate-900/50 border border-white/5 p-4">
                            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Owned</p>
                            <p className="text-2xl font-black text-amber-400 tabular-nums">{ownedCastles.length}</p>
                        </div>
                        <div className="rounded-xl bg-slate-900/50 border border-white/5 p-4">
                            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Upcoming Sieges</p>
                            <p className="text-2xl font-black text-red-400 tabular-nums">{upcomingSieges.length}</p>
                        </div>
                        <div className="rounded-xl bg-slate-900/50 border border-white/5 p-4">
                            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Fortresses</p>
                            <p className="text-2xl font-black text-white tabular-nums">{forts.length}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="container mx-auto px-4 pb-20">
                {/* Tabs */}
                <div className="flex items-center gap-2 mb-8">
                    <button
                        onClick={() => setTab("castles")}
                        className={`flex items-center gap-2 rounded-xl px-5 py-2.5 text-xs font-black uppercase tracking-widest transition-all ${
                            tab === "castles"
                                ? "bg-red-500/10 text-red-400 ring-1 ring-red-500/20"
                                : "text-slate-500 hover:text-white hover:bg-white/5"
                        }`}
                    >
                        <Castle className="h-4 w-4" />
                        Castles
                    </button>
                    <button
                        onClick={() => setTab("forts")}
                        className={`flex items-center gap-2 rounded-xl px-5 py-2.5 text-xs font-black uppercase tracking-widest transition-all ${
                            tab === "forts"
                                ? "bg-indigo-500/10 text-indigo-400 ring-1 ring-indigo-500/20"
                                : "text-slate-500 hover:text-white hover:bg-white/5"
                        }`}
                    >
                        <Landmark className="h-4 w-4" />
                        Fortresses
                    </button>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-32">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-red-500 border-t-transparent" />
                    </div>
                ) : tab === "castles" ? (
                    <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                        {castles.map((castle) => {
                            const isRegistered = !!myRegs.find((r) => r.castle_id === castle.id && r.clan_id === leaderInfo?.clan_id)
                            return (
                                <CastleCard
                                    key={castle.id}
                                    castle={castle}
                                    leaderClanId={leaderInfo?.clan_id ?? null}
                                    isRegistered={isRegistered}
                                    onRegister={onRegister}
                                    onUnregister={onUnregister}
                                    isAdmin={!!session?.user?.isAdmin}
                                    onSchedule={onSchedule}
                                />
                            )
                        })}
                    </div>
                ) : (
                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                        {forts.map((fort) => (
                            <FortCard key={fort.id} fort={fort} />
                        ))}
                        {forts.length === 0 && (
                            <div className="col-span-full text-center py-16 text-slate-600">
                                No fortress data available
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}
